import { useEffect, useRef, useState } from "react";
import { levelNum, parseFloorName, type BuildingDto, type PlanFile } from "@klantpunt/shared";
import { Modal } from "../../app/Modal";
import { api, ApiError } from "../../lib/api";
import { fileInfo, isPdf, isPlanImage, type FileInfo } from "../../lib/pdf";
import { useToast } from "../../lib/toast";
import { aspectDiff, uploadPlan } from "../../lib/upload";
import type { Zone } from "../../lib/zones";
import { zonesToFile } from "../../lib/zonesFile";
import { ZonePicker } from "./ZonePicker";
import { IconClose } from "../../app/icons";

interface Item {
  key: number;
  file: File;
  info: FileInfo | null;
  /** -1 = kon bladen niet tellen */
  pages: number;
  name: string;
  level: string;
  target: string; // "new" of floorId
  split: boolean;
  /** Meerdere verdiepingen op één blad: per kader een verdieping. */
  zones?: { page: number; zones: Zone[] };
  /** Reden waarom de vorige poging mislukte. */
  error?: string;
}

let seq = 0;
const pl = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** Bestanden die in de dialoog (of ergens op de pagina) worden losgelaten. */
export function ImportDialog({
  building,
  initialFiles,
  onClose,
  onDone,
  onPartial,
}: {
  building: BuildingDto;
  initialFiles: File[];
  onClose: () => void;
  onDone: (b: BuildingDto, firstNewFloorId: string | null) => void;
  /** Een deel is gelukt, de rest niet: gebouw bijwerken maar het venster openhouden. */
  onPartial?: (b: BuildingDto) => void;
}) {
  const [items, setItems] = useState<Item[]>([]);
  const [sort, setSort] = useState(true);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [over, setOver] = useState(false);
  const [failure, setFailure] = useState("");
  const [picking, setPicking] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const added = useRef(false);

  function addFiles(files: File[]) {
    const next: Item[] = [];
    for (const file of files) {
      if (!isPdf(file) && !isPlanImage(file)) {
        toast(`${file.name} overgeslagen: alleen PDF, PNG of JPG.`);
        continue;
      }
      const g = parseFloorName(file.name);
      const match = building.floors.find(
        (f) =>
          (g.level && f.level.toUpperCase() === g.level.toUpperCase()) || f.name.toLowerCase() === g.name.toLowerCase(),
      );
      next.push({
        key: ++seq,
        file,
        info: null,
        pages: isPdf(file) ? 0 : 1,
        name: g.name,
        level: g.level,
        target: match?.id ?? "new",
        split: false,
      });
    }
    setItems((cur) => [...cur, ...next].sort((a, b) => levelNum(a.level) - levelNum(b.level)));
    for (const it of next) {
      fileInfo(it.file).then(
        (info) => setItems((cur) => cur.map((x) => (x.key === it.key ? { ...x, info, pages: info.pages } : x))),
        () => setItems((cur) => cur.map((x) => (x.key === it.key ? { ...x, pages: -1 } : x))),
      );
    }
  }

  useEffect(() => {
    if (!added.current && initialFiles.length) addFiles(initialFiles);
    added.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Bestanden die tijdens het openstaan ergens op de pagina worden losgelaten.
  useEffect(() => {
    const onDrop = (e: Event) => addFiles((e as CustomEvent<File[]>).detail);
    window.addEventListener("pw-import-files", onDrop);
    return () => window.removeEventListener("pw-import-files", onDrop);
  });

  const update = (key: number, patch: Partial<Item>) =>
    setItems((cur) => cur.map((x) => (x.key === key ? { ...x, ...patch } : x)));
  const nNew = items.reduce(
    (s, it) =>
      s + (it.target === "new" ? (it.zones ? it.zones.zones.length : it.split && it.pages > 1 ? it.pages : 1) : 0),
    0,
  );
  const nRep = items.filter((it) => it.target !== "new").length;
  const counting = items.some((it) => it.pages === 0);

  async function run() {
    setBusy(true);
    let b = building;
    let firstNew: string | null = null;
    let addedN = 0;
    let replaced = 0;
    const failed = new Map<number, string>();
    setFailure("");
    for (const [k, it] of items.entries()) {
      setProgress(`${it.file.name} uploaden (${k + 1} van ${items.length})`);
      try {
        if (it.target === "new" && it.zones) {
          // Eén bijgesneden blad per zone in een nieuwe PDF; elke zone wordt een verdieping.
          setProgress(`${it.file.name}: zones uitsnijden`);
          const zfile = await zonesToFile(it.file, it.zones.page, it.zones.zones);
          setProgress(`${it.file.name} uploaden (${k + 1} van ${items.length})`);
          const plan: PlanFile = await uploadPlan(zfile, await fileInfo(zfile));
          for (const [i, z] of it.zones.zones.entries()) {
            const id = crypto.randomUUID();
            b = await api<BuildingDto>(`/buildings/${b.id}/floors`, {
              method: "POST",
              body: {
                id,
                name: z.name.trim() || `Zone ${i + 1}`,
                level: z.level.trim(),
                planFileId: plan.id,
                planPage: i + 1,
              },
            });
            firstNew ??= id;
            addedN++;
          }
          continue;
        }
        const info = it.info ?? (await fileInfo(it.file));
        const plan: PlanFile = await uploadPlan(it.file, info);
        if (it.target !== "new") {
          b = await api<BuildingDto>(`/floors/${it.target}`, {
            method: "PATCH",
            body: { planFileId: plan.id, planPage: 1 },
          });
          replaced++;
        } else if (it.split && info.pages > 1) {
          for (let p = 1; p <= info.pages; p++) {
            const id = crypto.randomUUID();
            b = await api<BuildingDto>(`/buildings/${b.id}/floors`, {
              method: "POST",
              body: {
                id,
                name: `${it.name} – blad ${p}`,
                level: it.level ? `${it.level}.${p}` : "",
                planFileId: plan.id,
                planPage: p,
              },
            });
            firstNew ??= id;
            addedN++;
          }
        } else {
          const id = crypto.randomUUID();
          b = await api<BuildingDto>(`/buildings/${b.id}/floors`, {
            method: "POST",
            body: {
              id,
              name: it.name.trim() || "Verdieping",
              level: it.level.trim(),
              planFileId: plan.id,
              planPage: 1,
            },
          });
          firstNew ??= id;
          addedN++;
        }
      } catch (e) {
        failed.set(it.key, e instanceof ApiError ? e.message : "Er ging iets mis");
      }
    }
    if (sort && b.floors.length > 1 && (addedN || replaced)) {
      setProgress("Verdiepingen op volgorde zetten");
      const ids = b.floors
        .map((f, i) => ({ f, i }))
        .sort((x, y) => levelNum(x.f.level) - levelNum(y.f.level) || x.i - y.i)
        .map((x) => x.f.id);
      b = await api<BuildingDto>(`/buildings/${b.id}/floors/reorder`, { method: "POST", body: { ids } });
    }
    const done = [
      addedN ? `${pl(addedN, "verdieping", "verdiepingen")} toegevoegd` : "",
      replaced ? `${pl(replaced, "tekening", "tekeningen")} vervangen` : "",
    ]
      .filter(Boolean)
      .join(" · ");
    if (failed.size) {
      // Venster openhouden met alleen de mislukte bestanden en de reden, zodat opnieuw proberen kan.
      if (addedN || replaced) onPartial?.(b);
      setItems((cur) => cur.filter((x) => failed.has(x.key)).map((x) => ({ ...x, error: failed.get(x.key) })));
      const reasons = [...new Set(failed.values())];
      setFailure(
        `${pl(failed.size, "bestand", "bestanden")} niet geïmporteerd${done ? ` (wel gelukt: ${done})` : ""}. ${reasons.length === 1 ? reasons[0] : ""}`.trim(),
      );
      setProgress("");
      setBusy(false);
      return;
    }
    toast(done || "Niets geïmporteerd");
    onDone(b, firstNew);
  }

  const pickItem = picking !== null ? items.find((x) => x.key === picking) : undefined;
  if (pickItem) {
    return (
      <ZonePicker
        file={pickItem.file}
        pages={Math.max(1, pickItem.pages)}
        initial={pickItem.zones ?? null}
        onCancel={() => setPicking(null)}
        onSave={(page, zones) => {
          update(pickItem.key, { zones: { page, zones }, split: false });
          setPicking(null);
        }}
      />
    );
  }

  return (
    <Modal
      title="Tekeningen importeren"
      wide
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <span className="hint">
            {items.length
              ? `${pl(nNew, "nieuwe verdieping", "nieuwe verdiepingen")}${nRep ? ` · ${pl(nRep, "tekening", "tekeningen")} vervangen` : ""}`
              : "Nog geen bestanden gekozen"}
          </span>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose} disabled={busy}>
            Annuleren
          </button>
          <button type="button" className="btn primary" onClick={run} disabled={!items.length || busy || counting}>
            {busy ? "Importeren…" : items.some((it) => it.error) ? "Opnieuw proberen" : "Importeren"}
          </button>
        </>
      }
    >
      <div
        className={`dropzone ${over ? "over" : ""}`}
        role="button"
        tabIndex={0}
        aria-label="Bestanden kiezen of hierheen slepen"
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), inputRef.current?.click())}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOver(false);
          if (!busy) addFiles([...e.dataTransfer.files]);
        }}
      >
        <strong>Sleep PDF's of afbeeldingen hierheen</strong>
        <span className="hint">
          of klik om bestanden te kiezen · meerdere tegelijk · elk bestand wordt een verdieping, of kies zones als er
          meerdere verdiepingen op één tekening staan
        </span>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,image/png,image/jpeg,image/webp"
        multiple
        hidden
        data-testid="import-input"
        onChange={(e) => {
          addFiles([...(e.target.files ?? [])]);
          e.target.value = "";
        }}
      />
      {items.length > 0 && (
        <>
          <div className="improws" role="table" aria-label="Te importeren bestanden">
            <div className="imphead" role="row">
              <span role="columnheader">Bestand</span>
              <span role="columnheader">Naam verdieping</span>
              <span role="columnheader">Afk.</span>
              <span role="columnheader">Wat gebeurt er</span>
              <span />
            </div>
            {items.map((it) => {
              const target = building.floors.find((f) => f.id === it.target);
              const diff = target?.plan && it.info ? aspectDiff(target.plan, it.info) : 0;
              return (
                <div className="improw" role="row" key={it.key}>
                  <span className="fn" role="cell">
                    <strong>{it.file.name}</strong>
                    <span className="hint">
                      {isPdf(it.file)
                        ? it.pages > 1
                          ? `${it.pages} bladen`
                          : it.pages === 1
                            ? "1 blad"
                            : it.pages < 0
                              ? "PDF"
                              : "bladen tellen…"
                        : "afbeelding"}{" "}
                      · {(it.file.size / 1048576).toFixed(1)} MB
                    </span>
                    {it.target === "new" && it.pages !== 0 && (
                      <span className="zonerow">
                        {it.zones ? (
                          <span>
                            {pl(it.zones.zones.length, "zone", "zones")}: {it.zones.zones.map((z) => z.name).join(", ")}
                          </span>
                        ) : null}
                        <button
                          type="button"
                          className="btn small"
                          disabled={busy}
                          onClick={() => setPicking(it.key)}
                          aria-label={`${it.zones ? "Zones wijzigen" : "Zones kiezen"} voor ${it.file.name}`}
                        >
                          {it.zones ? "Zones wijzigen" : "Zones kiezen"}
                        </button>
                        {it.zones && (
                          <button
                            type="button"
                            className="btn small"
                            disabled={busy}
                            onClick={() => update(it.key, { zones: undefined })}
                          >
                            Geen zones
                          </button>
                        )}
                      </span>
                    )}
                    {it.pages > 1 && it.target === "new" && !it.zones && (
                      <label className="split">
                        <input
                          type="checkbox"
                          checked={it.split}
                          onChange={(e) => update(it.key, { split: e.target.checked })}
                        />{" "}
                        elk blad een eigen verdieping
                      </label>
                    )}
                    {it.error && <span className="warn">{it.error}</span>}
                    {diff > 0.02 && (
                      <span className="warn">
                        Verhouding wijkt {Math.round(diff * 100)} % af: pins en ruimtes kunnen verschuiven.
                      </span>
                    )}
                  </span>
                  <input
                    aria-label="Naam verdieping"
                    value={it.name}
                    disabled={it.target !== "new" || Boolean(it.zones)}
                    onChange={(e) => update(it.key, { name: e.target.value })}
                  />
                  <input
                    aria-label="Afkorting"
                    value={it.level}
                    disabled={it.target !== "new" || Boolean(it.zones)}
                    onChange={(e) => update(it.key, { level: e.target.value })}
                  />
                  <select
                    aria-label="Bestemming"
                    value={it.target}
                    onChange={(e) => update(it.key, { target: e.target.value })}
                  >
                    <option value="new">Nieuwe verdieping</option>
                    {building.floors.map((f) => (
                      <option key={f.id} value={f.id}>
                        Vervang: {f.name}
                        {f.plan ? "" : " (nog geen tekening)"}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn ghost small"
                    aria-label={`${it.file.name} uit de lijst halen`}
                    onClick={() => setItems((cur) => cur.filter((x) => x.key !== it.key))}
                    disabled={busy}
                  >
                    <IconClose size={18} aria-hidden="true" />
                  </button>
                </div>
              );
            })}
          </div>
          <label className="check">
            <input type="checkbox" checked={sort} onChange={(e) => setSort(e.target.checked)} /> Verdiepingen daarna op
            volgorde zetten (kelder, BG, 1, 2 … dak)
          </label>
        </>
      )}
      {failure && !busy && (
        <div className="note danger-note" role="alert">
          {failure}
        </div>
      )}
      {busy && (
        <div className="note" role="status">
          <strong>Bezig…</strong> {progress}
        </div>
      )}
    </Modal>
  );
}
